/**
 * l1aStages — PURE derivation of the 12 L1A workflow-stage states for the merged
 * stage-bar + screen pills.
 *
 * This is a thin MAPPING layer over signals that already exist — it invents no
 * new data engine. It calls `deriveL1AStepStates` (the 5-step per-property
 * deriver) ONCE and `gridSummary` once, then projects the 11 template stages off
 * those plus a few extra context flags (advance cleared · intake received · the
 * two sign-off booleans · delivery timestamp), then projects the 12 template
 * stages off them (the 11 workflow stages + the Astro-Vastu confirm-view). Every
 * stage traces to exactly one source. No React, no Firestore — exercised
 * directly by the Node test runner,
 * exactly like `l1aSteps.ts`.
 *
 * The rich state vocabulary (`locked|blocked|ready|in_progress|done`) is reused
 * VERBATIM from l1aSteps — no new states. `toBarStatus()` projects it down to the
 * three visual states the bar dots + screen pills render.
 */
import {
  deriveL1AStepStates,
  type L1ACandidate,
  type L1AStepCtx,
  type L1AStepState,
  type L1AStepView,
} from './l1aSteps';
import { floorHasBoundary, type GriddableFloor } from './candidateGrid';
import { getEngagementTemplate } from './engagementTemplate';
import type { L1AStageKey, StageStatus } from './l1aTabs';

export type { L1ACandidate };

/** Rich stage state — identical to the 5-step vocabulary (no new states). */
export type L1AStageState = L1AStepState;

export interface L1AStageView {
  key: L1AStageKey;
  /** 1-based stage number (1…12). */
  n: number;
  label: string;
  state: L1AStageState;
  /** One-line collapsed status copy. */
  status: string;
}

export interface L1AStageCtx extends L1AStepCtx {
  /** effectiveClearedGateIds(p).includes('advance'). */
  advanceCleared: boolean;
  /** Intake form present (loadPropertiesIntake → a captured property set). */
  intakeReceived: boolean;
  /** project.ownerReviewed (manual sign-off today; default false). */
  ownerReviewed: boolean;
  /**
   * !!project.astro_vastu_confirmation — the staff "Confirm astro inputs" tick
   * (2026-07-03, owner ruling: confirming IS the tick; written from the
   * Astro-Vastu tab via lib/astroVastuConfirm). Optional so pre-existing
   * callers/tests read as false (pre-field docs default to unconfirmed).
   */
  astroConfirmed?: boolean;
  /**
   * `answers.recommended_property_id` is non-empty — a recommended property
   * has been SAVED (via the shared RecommendedPropertyPicker, either mount).
   * Owner ruling 2026-07-03 ("I selected A, even B — it does not tick"):
   * a saved recommendation IS the Compare stage's tick — the comparison has
   * been made. Mirrors the `astroConfirmed` pattern: optional so pre-existing
   * callers/tests read as false and keep the existing derivation.
   */
  recommendationSet?: boolean;
  /** project.closingCallDone (manual mark today; default false). */
  closingCallDone: boolean;
  /** Delivery timestamp (ISO) — drives the follow-up schedule. */
  deliveredAt?: string | null;
  /**
   * FUTURE auto seam — a completed closing-call task. Only consulted when the
   * closing_call stage is configured `autoDerive:true` (otherwise the manual
   * `closingCallDone` boolean wins). Undefined today.
   */
  closingCallLogged?: boolean;
  /** Epoch ms "now" for the follow-up elapsed check (default Date.now()). */
  nowMs?: number;
}

/** Per-stage config seam: flip a stage to its automatic source with ZERO call-site edits. */
export interface L1AStageConfig {
  key: L1AStageKey;
  autoDerive: boolean;
}

/**
 * Default config — the 9 fully-derived stages are `autoDerive:true`; the two
 * sign-off stages are manual today (`false`) and flip to auto later by changing
 * ONLY this flag (the derivation already implements both rules).
 */
export const L1A_STAGE_CONFIG: L1AStageConfig[] = [
  { key: 'intake', autoDerive: true },
  { key: 'intake_gate', autoDerive: true },
  // MERGED 2026-07-01 — the old `grids_charts` + `per_property` stages collapse
  // into one `evaluate` stage (grid-built check folded into the per-property
  // evaluation derivation).
  { key: 'evaluate', autoDerive: true },
  // Astro-Vastu — the staff confirm tick (`astro_vastu_confirmation`) drives
  // done; unconfirmed it derives from the (merged) evaluate state (bedrooms
  // are marked there). Non-blocking: it never gates comparative.
  { key: 'astro_vastu', autoDerive: true },
  { key: 'comparative', autoDerive: true },
  { key: 'verdict', autoDerive: true },
  { key: 'owner_review', autoDerive: false }, // flip → reportStatus drives it
  { key: 'rendered', autoDerive: true },
  { key: 'delivered', autoDerive: true },
  { key: 'closing_call', autoDerive: false }, // flip → a completed closing-call task
  { key: 'post_delivery_followup', autoDerive: true },
];

const L1A_TPL = getEngagementTemplate('L1A');
if (!L1A_TPL) throw new Error('l1aStages: getEngagementTemplate("L1A") returned null');
const STAGE_KEYS = L1A_TPL.stages.map((s) => s.key) as L1AStageKey[];
const LABEL_OF: Record<string, string> = Object.fromEntries(
  L1A_TPL.stages.map((s) => [s.key, s.label]),
);
const FOLLOW_UP = L1A_TPL.followUp;
const DAY_MS = 24 * 60 * 60 * 1000;

/** done → done · in_progress|ready → now · locked|blocked → todo. */
export function toBarStatus(state: L1AStageState): StageStatus {
  if (state === 'done') return 'done';
  if (state === 'in_progress' || state === 'ready') return 'now';
  return 'todo';
}

function floorsOf(c: L1ACandidate): GriddableFloor[] {
  return Array.isArray(c.floors) ? (c.floors as GriddableFloor[]) : [];
}

/**
 * Derive all 12 L1A stage states. Pure + total — any partial/empty ctx yields a
 * coherent set (no crash, no spurious greens).
 */
export function deriveL1AStageStates(
  ctx: L1AStageCtx,
  config: L1AStageConfig[] = L1A_STAGE_CONFIG,
): L1AStageView[] {
  const auto = (k: L1AStageKey) =>
    config.find((c) => c.key === k)?.autoDerive ?? false;

  const loading = !!ctx.loading || ctx.candidates == null;
  const cands = Array.isArray(ctx.candidates) ? ctx.candidates : [];
  const joinedCands = cands.filter((c) => !!c.propertyId);
  const reportStatus = ctx.reportStatus ?? null;

  // ── reuse every existing computation ONCE ──
  const stepViews = deriveL1AStepStates({
    candidates: ctx.candidates,
    propertyRefs: ctx.propertyRefs,
    recommendedId: ctx.recommendedId,
    reportStatus,
    loading: ctx.loading,
  });
  const sv = Object.fromEntries(stepViews.map((v) => [v.key, v])) as Record<
    L1AStepView['key'],
    L1AStepView
  >;
  const gridBuilt = joinedCands.filter((c) => floorsOf(c).some(floorHasBoundary)).length;

  const out: L1AStageView[] = [];
  const push = (key: L1AStageKey, state: L1AStageState, status: string) =>
    out.push({ key, n: STAGE_KEYS.indexOf(key) + 1, label: LABEL_OF[key] ?? key, state, status });

  // 1 — Intake
  {
    let state: L1AStageState;
    let status: string;
    if (loading) {
      state = 'ready';
      status = 'Loading candidate properties…';
    } else if (!ctx.intakeReceived && cands.length === 0) {
      state = 'blocked';
      status = 'Awaiting intake form';
    } else if (joinedCands.length === 0) {
      state = 'in_progress';
      status = `${cands.length} candidate${cands.length === 1 ? '' : 's'} — link each to the registry`;
    } else {
      state = 'done';
      status = `${joinedCands.length} candidate${joinedCands.length === 1 ? '' : 's'} linked`;
    }
    push('intake', state, status);
  }
  const intakeDone = out[0].state === 'done';

  // 2 — Intake gate (the advance money-gate)
  {
    let state: L1AStageState;
    let status: string;
    if (ctx.advanceCleared) {
      state = 'done';
      status = '100% advance cleared';
    } else if (intakeDone) {
      state = 'ready';
      status = 'Awaiting 100% advance';
    } else {
      state = 'locked';
      status = 'Complete intake first';
    }
    push('intake_gate', state, status);
  }

  // 3 — Evaluate (MERGED 2026-07-01: grids_charts + per_property → one stage).
  // The advance money-gate lock (formerly on grids_charts) is preserved here —
  // pre-payment the whole evaluate stage is locked. Once the advance is cleared,
  // the state IS the 5-step per_property_eval view (which already requires the
  // boundary/grid to be traced, subsuming the old grid-built check). The
  // grid-built count is folded into the collapsed status copy for continuity.
  {
    let state: L1AStageState;
    let status: string;
    if (!ctx.advanceCleared) {
      state = 'locked';
      status = 'Clear the advance payment first';
    } else if (joinedCands.length > 0 && gridBuilt === 0) {
      // Advance cleared but nothing traced yet — guide to the first action.
      state = sv.per_property_eval.state === 'locked' ? 'locked' : 'ready';
      status = 'Trace the boundary on each candidate plan';
    } else {
      // Grid work has begun → the per-property evaluation view is authoritative
      // (grid → entrance → AUO, per candidate).
      state = sv.per_property_eval.state;
      status = sv.per_property_eval.status;
    }
    push('evaluate', state, status);
  }
  const evaluateDone = out.find((o) => o.key === 'evaluate')?.state === 'done';

  // 4 — Astro-Vastu (confirm view + WRITE, 2026-07-03). NON-BLOCKING: it never
  // gates comparative. The staff "Confirm astro inputs" tick — the persisted
  // `astro_vastu_confirmation` on the project doc — flips this stage to done
  // (owner ruling: confirming IS the tick). A confirmation wins even if the
  // Evaluate stage later reopens: the tick snapshots what was confirmed at the
  // time. Unconfirmed: ready once Evaluate is done, locked before.
  {
    let state: L1AStageState;
    let status: string;
    if (ctx.astroConfirmed) {
      state = 'done';
      status = 'Astro inputs confirmed';
    } else if (evaluateDone) {
      state = 'ready';
      status = 'Confirm family charts against the marked bedrooms';
    } else {
      state = 'locked';
      status = 'Mark bedrooms in Evaluate first';
    }
    push('astro_vastu', state, status);
  }

  // 5 — Comparative. A SAVED recommended property flips the stage to done
  // (owner ruling 2026-07-03: picking the winner IS the comparison's tick —
  // the multi-property compare otherwise never left 'ready'). Without a
  // saved pick it stays the 5-step comparative view, verbatim.
  if (ctx.recommendationSet) {
    push('comparative', 'done', 'Recommendation set');
  } else {
    push('comparative', sv.comparative.state, sv.comparative.status);
  }
  // 6 — Verdict (= the 5-step verdict view, verbatim)
  push('verdict', sv.verdict.state, sv.verdict.status);
  const verdictDone = sv.verdict.state === 'done';

  // 7 — Owner review (manual now / report-driven when autoDerive)
  {
    let state: L1AStageState;
    let status: string;
    if (auto('owner_review')) {
      if (reportStatus === 'approved' || reportStatus === 'delivered') {
        state = 'done';
        status = 'Owner signed off';
      } else if (reportStatus === 'draft') {
        state = 'ready';
        status = 'Ready for owner review';
      } else {
        state = 'locked';
        status = 'Confirm the verdict first';
      }
    } else if (ctx.ownerReviewed) {
      state = 'done';
      status = 'Owner signed off';
    } else if (verdictDone) {
      state = 'ready';
      status = 'Ready for owner review';
    } else {
      state = 'locked';
      status = 'Confirm the verdict first';
    }
    push('owner_review', state, status);
  }
  // Key-based lookup (index-independent — robust to stage-chain edits such as
  // the 2026-07-01 grids_charts+per_property → evaluate merge).
  const ownerReviewedEffective =
    out.find((o) => o.key === 'owner_review')?.state === 'done';

  // 8 — Report render
  {
    let state: L1AStageState;
    let status: string;
    if (!ownerReviewedEffective) {
      state = 'locked';
      status = 'Owner sign-off required first';
    } else if (reportStatus === 'approved' || reportStatus === 'delivered') {
      state = 'done';
      status = 'Report approved';
    } else if (reportStatus === 'draft') {
      state = 'in_progress';
      status = 'Draft generated — review';
    } else {
      state = 'ready';
      status = 'Generate the report';
    }
    push('rendered', state, status);
  }

  // 9 — Delivered
  {
    let state: L1AStageState;
    let status: string;
    if (reportStatus === 'delivered') {
      state = 'done';
      status = 'Delivered';
    } else if (reportStatus === 'approved') {
      state = 'ready';
      status = 'Approved — ready to deliver';
    } else {
      state = 'locked';
      status = 'Approve the report first';
    }
    push('delivered', state, status);
  }
  const delivered = reportStatus === 'delivered';

  // 10 — Closing call (manual now / task-driven when autoDerive)
  {
    let state: L1AStageState;
    let status: string;
    const doneFlag = auto('closing_call') ? !!ctx.closingCallLogged : ctx.closingCallDone;
    if (doneFlag) {
      state = 'done';
      status = 'Closing call done';
    } else if (delivered) {
      state = 'ready';
      status = 'Schedule the closing call';
    } else {
      state = 'locked';
      status = 'Available after delivery';
    }
    push('closing_call', state, status);
  }

  // 11 — Post-delivery follow-up
  {
    let state: L1AStageState;
    let status: string;
    if (!delivered) {
      state = 'locked';
      status = 'Available after delivery';
    } else {
      const now = ctx.nowMs ?? Date.now();
      const delMs = ctx.deliveredAt ? Date.parse(ctx.deliveredAt) : NaN;
      const allElapsed =
        !Number.isNaN(delMs) &&
        FOLLOW_UP.every((f) => delMs + f.dayOffset * DAY_MS <= now);
      if (FOLLOW_UP.length > 0 && allElapsed) {
        state = 'done';
        status = 'All touchpoints complete';
      } else {
        state = 'in_progress';
        const next = FOLLOW_UP.find(
          (f) => Number.isNaN(delMs) || delMs + f.dayOffset * DAY_MS > now,
        );
        status = next ? `Follow-up scheduled (Day-${next.dayOffset})` : 'Follow-up scheduled';
      }
    }
    push('post_delivery_followup', state, status);
  }

  return out;
}

/** First stage that is NOT done and NOT locked (else first non-done, else last). */
export function firstOpenStage(views: L1AStageView[]): L1AStageKey {
  const actionable = views.find((v) => v.state !== 'done' && v.state !== 'locked');
  if (actionable) return actionable.key;
  const notDone = views.find((v) => v.state !== 'done');
  if (notDone) return notDone.key;
  return views[views.length - 1]?.key ?? 'intake';
}
